import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Header from "../generic/Header";
import ConfirmationModal from "./ConfirmationModal";
import scheduleService from '../../services/scheduleService';
import participantsService from '../../services/participantsService';
import { categoriesService, ageGroupsService, pdfExportService, groupsService } from '../../services';
import settingsService from '../../services/settingsService';
import cacheService from '../../utils/cacheService';
import { getAgeGroupColors } from "../../utils/ageGroupColors";

const ScheduleTailwind = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDay, setSelectedDay] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedGender, setSelectedGender] = useState("all");
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);
  
  // Venue-Struktur
  const [venuesData, setVenuesData] = useState({
    "Fläche A": [],
    "Fläche B": []
  });
  
  // State für Änderungsverfolgung
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [changeHistory, setChangeHistory] = useState([]);
  
  // Drag & Drop State für Events zwischen Venues
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [draggedFromVenue, setDraggedFromVenue] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverVenue, setDragOverVenue] = useState(null);

  const [sortingEventId, setSortingEventId] = useState(null);
  const [participantOrder, setParticipantOrder] = useState({});
  const [nationOrder, setNationOrder] = useState({});
  const [teamData, setTeamData] = useState(null); // Team-Daten für Team-Events
  const [expandedNations, setExpandedNations] = useState(new Set()); // Erweiterte Nationen in Team-Events
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [hasExistingEvents, setHasExistingEvents] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState('generate');
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [isStartingTournament, setIsStartingTournament] = useState(false);
  
  // PDF Export States
  const [isExporting, setIsExporting] = useState(false);
  
  // Timeline View State
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'timeline'

  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadScheduleData();
    loadParticipants();
    loadCategoriesAndAgeGroups();
    checkExistingEvents();
  }, []);

  // Lade Kategorien und Altersgruppen (mit Cache)
  const loadCategoriesAndAgeGroups = async () => {
    try {
      // Prüfe Cache zuerst
      const cachedCategories = cacheService.get('categories');
      const cachedAgeGroups = cacheService.get('ageGroups');

      if (cachedCategories && cachedAgeGroups) {
        setCategories(cachedCategories);
        setAgeGroups(cachedAgeGroups);
        return; // Verwende Cache, kein Request nötig
      }

      // Cache nicht vorhanden oder abgelaufen, lade vom Backend
      const [categoriesResult, ageGroupsResult] = await Promise.all([
        categoriesService.getAllCategories(),
        ageGroupsService.getAllAgeGroups()
      ]);

      if (categoriesResult.success) {
        setCategories(categoriesResult.data);
        cacheService.set('categories', categoriesResult.data);
      } else {
        console.error('Fehler beim Laden der Kategorien:', categoriesResult.error);
      }

      if (ageGroupsResult.success) {
        setAgeGroups(ageGroupsResult.data);
        cacheService.set('ageGroups', ageGroupsResult.data);
      } else {
        console.error('Fehler beim Laden der Altersgruppen:', ageGroupsResult.error);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien und Altersgruppen:', error);
    }
  };

  // Prüfe ob bereits Events existieren
  const checkExistingEvents = async () => {
    try {
      const result = await scheduleService.hasExistingEvents();
      if (result.success) {
        setHasExistingEvents(result.data.hasEvents);
      }
    } catch (error) {
      console.error('Fehler beim Prüfen der Events:', error);
    }
  };

  // Hilfsfunktion: Erstelle Mittagspause-Events für einen Tag
  const createLunchBreakEvents = (events, settings) => {
    if (!settings || !settings.lunch_start_time || !settings.lunch_duration_minutes) {
      return [];
    }

    const lunchStartTime = settings.lunch_start_time.value || '12:30';
    const lunchDurationMinutes = parseInt(settings.lunch_duration_minutes.value || '60', 10);

    // Gruppiere Events nach Tag
    const eventsByDay = {};
    events.forEach(event => {
      if (event.startTime) {
        const eventDate = new Date(event.startTime);
        const dayKey = eventDate.toISOString().split('T')[0]; // YYYY-MM-DD
        if (!eventsByDay[dayKey]) {
          eventsByDay[dayKey] = [];
        }
        eventsByDay[dayKey].push(event);
      }
    });

    // Erstelle Mittagspause-Events für jeden Tag
    const lunchEvents = [];
    Object.keys(eventsByDay).forEach(dayKey => {
      const [hours, minutes] = lunchStartTime.split(':').map(Number);
      const lunchStart = new Date(dayKey);
      lunchStart.setHours(hours, minutes, 0, 0);
      const lunchEnd = new Date(lunchStart);
      lunchEnd.setMinutes(lunchEnd.getMinutes() + lunchDurationMinutes);

      // Erstelle Mittagspause-Event für beide Flächen
      ['Fläche A', 'Fläche B'].forEach(venue => {
        lunchEvents.push({
          id: `lunch-${dayKey}-${venue}`,
          venue: venue,
          startTime: lunchStart.toISOString(),
          endTime: lunchEnd.toISOString(),
          status: 'Ausstehend',
          isLunchBreak: true,
          group: null
        });
      });
    });

    return lunchEvents;
  };

  // Lade Zeitplan-Daten
  const loadScheduleData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await scheduleService.getAllScheduleEventsWithFallback();
      
      if (result.success) {
        setScheduleData(result.data);
        
        // Lade Settings für Mittagspause
        let lunchEvents = [];
        try {
          const settingsResult = await settingsService.getAllSettings();
          if (settingsResult.success && settingsResult.data) {
            lunchEvents = createLunchBreakEvents(result.data, settingsResult.data);
          }
        } catch (error) {
          console.debug('Fehler beim Laden der Settings für Mittagspause (nicht kritisch):', error);
        }
        
        // Kombiniere normale Events mit Mittagspause-Events und sortiere nach Zeit
        const allEvents = [...result.data, ...lunchEvents].sort((a, b) => {
          return new Date(a.startTime) - new Date(b.startTime);
        });
        
        // Aktualisiere venuesData basierend auf allen Events
        const newVenuesData = {
          "Fläche A": allEvents.filter(item => item.venue === "Fläche A"),
          "Fläche B": allEvents.filter(item => item.venue === "Fläche B")
        };
        setVenuesData(newVenuesData);
        
        // Prüfe ob Team-Events vorhanden sind und lade Team-Daten
        const hasTeamEvent = result.data.some(event => 
          event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb'
        );
        
        if (hasTeamEvent) {
          try {
            const teamGroupResponse = await groupsService.getTeamCompetitionGroup();
            if (teamGroupResponse.success && teamGroupResponse.data) {
              setTeamData(teamGroupResponse.data);
            }
          } catch (error) {
            console.debug('Fehler beim Laden der Team-Daten (nicht kritisch):', error);
          }
        }
      } else {
        setError(result.error);
        console.error('Fehler beim Laden des Zeitplans:', result.error);
      }
    } catch (error) {
      setError('Fehler beim Laden des Zeitplans');
      console.error('Fehler beim Laden des Zeitplans:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lade Teilnehmer-Daten
  const loadParticipants = async () => {
    try {
      const result = await participantsService.getAllParticipants();
      if (result.success) {
        setParticipants(result.data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Teilnehmer:', error);
    }
  };

  // Teilnehmer für ein Event finden
  const getParticipantsForEvent = (event) => {
    if (!event.group) return [];
    
    // Spezielle Behandlung für Team-Events (Gruppenwettbewerb)
    const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');
    
    if (isTeamEvent && teamData && teamData.teams) {
      // Sammle alle Teilnehmer aus allen Teams
      const allTeamParticipants = [];
      teamData.teams.forEach(team => {
        if (team.participants && Array.isArray(team.participants)) {
          allTeamParticipants.push(...team.participants);
        }
      });
      return allTeamParticipants;
    }
    
    // Normale Gruppen: Filtere über ParticipantGroup
    if (!participants.length) return [];
    
    const eventParticipants = participants.filter(participant => {
      if (!participant.groups || !Array.isArray(participant.groups)) {
        return false;
      }
      return participant.groups.some(group => group.id === event.group.id);
    });
    
    // Sortiere nach Startnummer
    eventParticipants.sort((a, b) => {
      const aGroup = a.groups.find(g => g.id === event.group.id);
      const bGroup = b.groups.find(g => g.id === event.group.id);
      const aStartNumber = aGroup ? aGroup.startNumber : 999;
      const bStartNumber = bGroup ? bGroup.startNumber : 999;
      return aStartNumber - bStartNumber;
    });
    
    return eventParticipants;
  };

  // Event Handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDayChange = (day) => {
    setSelectedDay(day);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
  };

  const handleToggleExpand = async (eventId) => {
    const newExpandedEvents = new Set(expandedEvents);
    const event = scheduleData.find(e => e.id === eventId);
    const isTeamEvent = !!(event?.group?.isTeamCompetition || event?.group?.id === 'team-competition' || event?.group?.name === 'Gruppenwettbewerb');
    
    if (newExpandedEvents.has(eventId)) {
      newExpandedEvents.delete(eventId);
      // Lösche nur erweiterte Nationen, aber behalte teamData
      // Team-Daten bleiben erhalten, damit Teilnehmeranzahl korrekt angezeigt wird
      setExpandedNations(new Set());
    } else {
      newExpandedEvents.add(eventId);
      // Lade Team-Daten wenn Team-Event erweitert wird (nur wenn noch nicht vorhanden)
      if (isTeamEvent && !teamData) {
        try {
          const teamGroupResponse = await groupsService.getTeamCompetitionGroup();
          if (teamGroupResponse.success && teamGroupResponse.data) {
            setTeamData(teamGroupResponse.data);
          }
        } catch (error) {
          console.error('Fehler beim Laden der Team-Daten:', error);
        }
      }
    }
    setExpandedEvents(newExpandedEvents);
  };

  const handleToggleNationExpansion = (nationId) => {
    const newExpandedNations = new Set(expandedNations);
    if (newExpandedNations.has(nationId)) {
      newExpandedNations.delete(nationId);
    } else {
      newExpandedNations.add(nationId);
    }
    setExpandedNations(newExpandedNations);
  };

  const handleExpandAll = () => {
    const allEventIds = filteredSchedule.map(event => event.id);
    setExpandedEvents(new Set(allEventIds));
  };

  const handleCollapseAll = () => {
    setExpandedEvents(new Set());
  };

  const handleToggleEditMode = () => {
    const newEditMode = !isEditMode;
    setIsEditMode(newEditMode);
    if (isEditMode) {
      if (hasUnsavedChanges) {
        addToast('warning', 'Ungespeicherte Änderungen wurden verworfen');
      }
      loadScheduleData();
      setExpandedEvents(new Set());
      setNationOrder({});
      setHasUnsavedChanges(false);
      setChangeHistory([]);
    }
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const changes = {
        events: scheduleData,
        venuesData: venuesData,
        groupPerformanceOrders: nationOrder,
        changeHistory: changeHistory,
        timestamp: new Date().toISOString()
      };
      
      const result = await scheduleService.saveScheduleChanges(changes);
      
      if (result.success) {
        addToast('success', result.message || 'Änderungen erfolgreich gespeichert');
        await loadScheduleData();
        setIsEditMode(false);
        setExpandedEvents(new Set());
        setNationOrder({});
        setHasUnsavedChanges(false);
        setChangeHistory([]);
        
        // Reset drag state
        setDraggedEvent(null);
        setDraggedFromVenue(null);
        setDraggedIndex(null);
        setDragOverIndex(null);
        setDragOverVenue(null);
      } else {
        addToast('error', result.error || 'Fehler beim Speichern der Änderungen');
      }
    } catch (error) {
      addToast('error', 'Fehler beim Speichern der Änderungen');
    } finally {
      setIsSaving(false);
    }
  };

  // Drag & Drop Funktionalität für Events zwischen Venues
  const handleScheduleEventDragStart = (e, event, venue, index) => {
    let actualIndex = index;
    if (isEditMode) {
      const originalEvents = venuesData[venue];
      actualIndex = originalEvents.findIndex(e => e.id === event.id);
    }
    
    setDraggedEvent(event);
    setDraggedFromVenue(venue);
    setDraggedIndex(actualIndex);
    e.dataTransfer.effectAllowed = "move";
    
    if (e.target) {
      e.dataTransfer.setDragImage(e.target, 0, 0);
    }
  };

  const handleScheduleEventDragOver = (e, venue, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverVenue(venue);
    setDragOverIndex(index);
  };

  const handleScheduleEventDragLeave = () => {
    setDragOverIndex(null);
    setDragOverVenue(null);
  };

  const handleScheduleEventDrop = async (e, targetVenue, targetIndex) => {
    e.preventDefault();
    
    if (!draggedEvent || draggedFromVenue === null || draggedIndex === null) {
      return;
    }

    try {
      const newVenuesData = { ...venuesData };
      const sourceVenue = newVenuesData[draggedFromVenue];
      const targetVenueEvents = newVenuesData[targetVenue];
      
      // Entferne aus Quelle
      const [movedEvent] = sourceVenue.splice(draggedIndex, 1);
      movedEvent.venue = targetVenue;
      
      // Füge zu Ziel hinzu
      targetVenueEvents.splice(targetIndex, 0, movedEvent);
      
      // Aktualisiere sequenceNumber für alle Events in beiden Venues
      const updateSequenceNumbers = (venueEvents) => {
        venueEvents.forEach((event, index) => {
          event.sequenceNumber = index + 1;
        });
      };
      
      updateSequenceNumbers(newVenuesData[draggedFromVenue]);
      updateSequenceNumbers(newVenuesData[targetVenue]);
      
      // Aktualisiere lokalen State sofort
      setVenuesData(newVenuesData);
      
      // Aktualisiere auch scheduleData für Konsistenz
      const newScheduleData = scheduleData.map(event => {
        if (event.id === draggedEvent.id) {
          return { ...event, venue: targetVenue, sequenceNumber: targetIndex + 1 };
        }
        const venueEvents = newVenuesData[event.venue];
        if (venueEvents) {
          const eventInVenue = venueEvents.find(e => e.id === event.id);
          if (eventInVenue) {
            return { ...event, sequenceNumber: eventInVenue.sequenceNumber };
          }
        }
        return event;
      });
      setScheduleData(newScheduleData);
      
      // Markiere als ungespeicherte Änderung
      setHasUnsavedChanges(true);
      setChangeHistory(prev => [...prev, {
        type: 'event_moved',
        eventId: draggedEvent.id,
        fromVenue: draggedFromVenue,
        toVenue: targetVenue,
        fromIndex: draggedIndex,
        toIndex: targetIndex,
        timestamp: new Date().toISOString()
      }]);
      
      // API-Call im Hintergrund
      try {
        const newSequenceNumber = targetIndex + 1;
        await scheduleService.moveScheduleEvent(
          draggedEvent.id, 
          targetVenue, 
          newSequenceNumber
        );
      } catch (error) {
        console.error('Fehler beim Server-Update:', error);
      }
      
    } catch (error) {
      console.error('Fehler beim Verschieben:', error);
    }

    // Reset drag state
    setDraggedEvent(null);
    setDraggedFromVenue(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverVenue(null);
  };

  const handleScheduleEventDragEnd = () => {
    setDraggedEvent(null);
    setDraggedFromVenue(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverVenue(null);
  };

  // Generiere dayOptions dynamisch basierend auf den vorhandenen Events
  const dayOptions = useMemo(() => {
    const days = new Set();
    
    scheduleData.forEach(event => {
      if (event.startTime) {
        const day = event.startTime.split('T')[0];
        days.add(day);
      }
    });
    
    const sortedDays = Array.from(days).sort();
    
    const options = [
      { value: "all", label: t("admin.schedule.allDays", "Alle Tage") }
    ];
    
    sortedDays.forEach(day => {
      const date = new Date(day);
      const formattedDate = date.toLocaleDateString('de-DE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      options.push({
        value: day,
        label: formattedDate
      });
    });
    
    return options;
  }, [scheduleData, t]);

  const categoryOptions = [
    { value: "all", label: t("admin.schedule.allCategories", "Alle Kategorien") },
    ...categories.map((category) => ({
      value: category.name,
      label: category.name
    }))
  ];

  const statusOptions = [
    { value: "all", label: t("admin.schedule.allStatuses", "Alle Status") },
    { value: "Ausstehend", label: t("admin.schedule.pending", "Ausstehend") },
    { value: "Aktiv", label: t("admin.schedule.active", "Aktiv") },
    { value: "Abgeschlossen", label: t("admin.schedule.completed", "Abgeschlossen") }
  ];

  const genderOptions = [
    { value: "all", label: t("admin.schedule.allGenders", "Alle Geschlechter") },
    { value: "männlich", label: "Männlich" },
    { value: "weiblich", label: "Weiblich" },
    { value: "gemischt", label: "Gemischt" }
  ];

  const filteredSchedule = scheduleData.filter((item) => {
    const matchesSearch = (item.group?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.venue || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDay = selectedDay === "all" || item.startTime?.startsWith(selectedDay);
    const matchesCategory = selectedCategory === "all" || item.group?.category?.name === selectedCategory;
    const matchesStatus = selectedStatus === "all" || item.status === selectedStatus;
    const matchesGender = selectedGender === "all" || item.group?.gender === selectedGender;
    return matchesSearch && matchesDay && matchesCategory && matchesStatus && matchesGender;
  });

  // Berechne die tatsächliche Dauer für eine Gruppe
  const calculateGroupDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '0min';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    } else {
      return `${minutes}min`;
    }
  };

  // Berechne die tatsächliche Dauer in Minuten für eine Gruppe
  const calculateGroupDurationMinutes = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    return Math.floor(durationMs / (1000 * 60));
  };

  // Berechne Gesamtanzahl der Athleten pro Fläche
  const getTotalParticipants = (events) => {
    return events.reduce((total, event) => total + getParticipantsForEvent(event).length, 0);
  };

  // Berechne reine Wettkampfzeit basierend auf der Summe aller Event-Dauern
  const calculateTimeEstimate = (events) => {
    if (events.length === 0) return '0min';
    
    let totalMinutes = 0;
    
    events.forEach(event => {
      if (event.startTime && event.endTime) {
        const start = new Date(event.startTime);
        const end = new Date(event.endTime);
        const durationMs = end.getTime() - start.getTime();
        const durationMinutes = Math.floor(durationMs / (1000 * 60));
        totalMinutes += durationMinutes;
      }
    });
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    } else {
      return `${minutes}min`;
    }
  };

  // Timeline-spezifische Hilfsfunktionen
  const getTimelineData = () => {
    const allEvents = [];
    
    Object.entries(displayVenuesData).forEach(([venue, events]) => {
      events.forEach(event => {
        allEvents.push({
          ...event,
          venue,
          startTime: new Date(event.startTime),
          endTime: new Date(event.endTime),
          duration: calculateGroupDurationMinutes(event.startTime, event.endTime)
        });
      });
    });
    
    // Sortiere nach Startzeit
    allEvents.sort((a, b) => a.startTime - b.startTime);
    
    // Finde den frühesten und spätesten Zeitpunkt
    const earliestTime = allEvents.length > 0 ? Math.min(...allEvents.map(e => e.startTime.getTime())) : new Date().getTime();
    const latestTime = allEvents.length > 0 ? Math.max(...allEvents.map(e => e.endTime.getTime())) : new Date().getTime();
    
    // Berechne die optimale Timeline-Höhe basierend auf der Gesamtdauer
    const totalDurationMinutes = (latestTime - earliestTime) / (1000 * 60);
    const minHeightPerMinute = 20; // 20px pro Minute für bessere Sichtbarkeit
    const timelineHeight = Math.max(totalDurationMinutes * minHeightPerMinute, 400); // Mindestens 400px
    
    return {
      events: allEvents,
      earliestTime: new Date(earliestTime),
      latestTime: new Date(latestTime),
      totalDuration: latestTime - earliestTime,
      timelineHeight: timelineHeight
    };
  };

  const getEventPosition = (event, timelineData) => {
    const startTime = new Date(event.startTime);
    const endTime = new Date(event.endTime);
    const startOffset = startTime.getTime() - timelineData.earliestTime.getTime();
    const duration = endTime.getTime() - startTime.getTime();
    
    return {
      left: (startOffset / timelineData.totalDuration) * 100,
      width: (duration / timelineData.totalDuration) * 100
    };
  };

  const formatTimeForTimeline = (date) => {
    return date.toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Erstelle gefilterte Version von venuesData für die Anzeige
  const filteredVenuesData = useMemo(() => {
    const filtered = {
      "Fläche A": [],
      "Fläche B": []
    };

    Object.entries(venuesData).forEach(([venue, events]) => {
      filtered[venue] = events.filter(event => {
        const matchesSearch = (event.group?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (event.venue || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDay = selectedDay === "all" || event.startTime?.startsWith(selectedDay);
        const matchesCategory = selectedCategory === "all" || event.group?.category?.name === selectedCategory;
        const matchesStatus = selectedStatus === "all" || event.status === selectedStatus;
        const matchesGender = selectedGender === "all" || event.group?.gender === selectedGender;
        return matchesSearch && matchesDay && matchesCategory && matchesStatus && matchesGender;
      });
    });

    return filtered;
  }, [venuesData, searchTerm, selectedDay, selectedCategory, selectedStatus, selectedGender]);

  // Verwende gefilterte Daten für die Anzeige, aber behalte ursprüngliche venuesData für Drag & Drop
  const displayVenuesData = isEditMode ? venuesData : filteredVenuesData;

  const getStatusLabel = (status) => {
    switch (status) {
      case "Ausstehend":
        return t("admin.schedule.pending", "Ausstehend");
      case "Aktiv":
        return t("admin.schedule.active", "Aktiv");
      case "Abgeschlossen":
        return t("admin.schedule.completed", "Abgeschlossen");
      case "Vorbereitung":
        return t("admin.schedule.preparation", "Vorbereitung");
      default:
        return status;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    return date.toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Formatiere das Datum für die Anzeige
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('de-DE', options);
  };

  // Handler zum Aktivieren des Sortiermodus
  const handleStartSorting = (eventId, participants) => {
    setSortingEventId(eventId);
    setParticipantOrder({ ...participantOrder, [eventId]: [...participants] });
    
    if (!expandedEvents.has(eventId)) {
      const newExpandedEvents = new Set(expandedEvents);
      newExpandedEvents.add(eventId);
      setExpandedEvents(newExpandedEvents);
    }
  };

  // Handler zum Abbrechen
  const handleCancelSorting = () => {
    setSortingEventId(null);
    setParticipantOrder({});
  };

  // Handler zum Speichern
  const handleSaveSorting = (eventId) => {
    setSortingEventId(null);
    setParticipantOrder({});
  };

  // Funktion zum Gruppieren von Teilnehmern nach Nationen für Gruppenperformances
  const groupParticipantsByNation = (participants, event) => {
    const grouped = {};
    participants.forEach(participant => {
      const nationName = participant.nation?.name || 'Unbekannt';
      if (!grouped[nationName]) {
        grouped[nationName] = [];
      }
      grouped[nationName].push(participant);
    });
    
    Object.keys(grouped).forEach(nation => {
      grouped[nation].sort((a, b) => {
        const aGroup = a.groups.find(g => g.id === event?.group?.id);
        const bGroup = b.groups.find(g => g.id === event?.group?.id);
        const aStartNumber = aGroup ? aGroup.startNumber : 999;
        const bStartNumber = bGroup ? bGroup.startNumber : 999;
        return aStartNumber - bStartNumber;
      });
    });
    
    return grouped;
  };

  // Funktion zum Rendern der Teilnehmerliste
  const renderParticipantsList = (eventId, participants) => {
    const event = scheduleData.find(e => e.id === eventId);
    const isTeamEvent = !!(event?.group?.isTeamCompetition || event?.group?.id === 'team-competition' || event?.group?.name === 'Gruppenwettbewerb');
    const isGroupPerformance = event?.group?.category?.name === "Gruppenperformance";
    
    // Spezielle Darstellung für Team-Events (Gruppenwettbewerb)
    if (isTeamEvent && teamData && teamData.teams) {
      return (
        <div className="mt-4 space-y-4">
          <h5 className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
            Teams ({teamData.teams.length} Nationen, {teamData.currentParticipants || 0} Teilnehmer)
          </h5>
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {teamData.teams.map((team) => {
                const isExpanded = expandedNations.has(team.nationId);
                return (
                  <div key={team.nationId} className="transition-colors">
                    {/* Nationen-Header (ausklappbar) */}
                    <button
                      onClick={() => handleToggleNationExpansion(team.nationId)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                          <i className="fas fa-chevron-right text-slate-400 dark:text-slate-500 text-sm"></i>
                        </div>
                        {team.startNumber && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary dark:bg-primary-light text-white">
                            #{team.startNumber}
                          </span>
                        )}
                        <img
                          src={process.env.PUBLIC_URL + "/images/flags/" + (team.nationCode || 'DE') + ".png"}
                          alt={team.nationName}
                          className="w-8 h-8 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{team.nationName}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{team.teamSize} Teilnehmer</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                          <i className="fas fa-users text-[10px]"></i>
                          Team
                        </span>
                      </div>
                    </button>

                    {/* Ausklappbare Teilnehmer-Liste */}
                    {isExpanded && team.participants && (
                      <div className="bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                        <div className="px-6 py-4">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                              <thead className="bg-white dark:bg-slate-800">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">#</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Teilnehmer</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Alter</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Geschlecht</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                                {team.participants.map((participant, index) => (
                                  <tr key={participant.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                                      <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold">
                                        {participant.participantNumber || index + 1}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">{participant.name}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">{participant.age || '-'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                                      {participant.gender === 'männlich' ? 'M' : participant.gender === 'weiblich' ? 'W' : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }
    
    if (isGroupPerformance) {
      const groupedParticipants = groupParticipantsByNation(participants, event);
      
      if (!nationOrder[eventId]) {
        setNationOrder(prev => ({
          ...prev,
          [eventId]: Object.keys(groupedParticipants)
        }));
        return null;
      }
      
      const currentNationOrder = nationOrder[eventId];
      
      const handleNationDragStart = (e, nation) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', nation);
      };

      const handleNationDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      };

      const handleNationDrop = (e, targetNation) => {
        e.preventDefault();
        const draggedNation = e.dataTransfer.getData('text/plain');
        if (draggedNation === targetNation) return;

        const newOrder = [...currentNationOrder];
        const draggedIndex = newOrder.indexOf(draggedNation);
        const targetIndex = newOrder.indexOf(targetNation);
        
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedNation);
        
        setNationOrder(prev => ({
          ...prev,
          [eventId]: newOrder
        }));
      };

      return (
        <div className="mt-4 space-y-4">
          <h5 className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
            Teams nach Nationen
            {isEditMode && (
              <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">
                <i className="fas fa-info-circle"></i> Drag & Drop zum Ändern der Nationen-Reihenfolge
              </span>
            )}
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentNationOrder.map((nation, index) => {
              const teamParticipants = groupedParticipants[nation];
              return (
                <div 
                  key={nation} 
                  className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 ${isEditMode ? 'cursor-move hover:bg-slate-50 dark:hover:bg-slate-700' : ''} transition-colors`}
                  draggable={isEditMode}
                  onDragStart={(e) => handleNationDragStart(e, nation)}
                  onDragOver={handleNationDragOver}
                  onDrop={(e) => handleNationDrop(e, nation)}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={process.env.PUBLIC_URL + "/images/flags/" + (teamParticipants[0]?.nation?.code || 'DE') + ".png"}
                      alt={nation}
                      className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 dark:text-slate-100">{nation}</div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">{teamParticipants.length} Athleten</div>
                    </div>
                    {isEditMode && (
                      <div className="text-xs text-slate-400 dark:text-slate-500">#{index + 1}</div>
                    )}
                  </div>
                  <div className="space-y-2">
                    {teamParticipants.map((participant, participantIndex) => (
                      <div key={participant.id} className="flex items-center gap-2 text-sm">
                        <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold">
                          #{participant.participantNumber}
                        </span>
                        <span className="text-slate-900 dark:text-slate-100">{participant.name}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">#{participantIndex + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    } else {
      // Normale Teilnehmerliste für reguläre Events
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {sortingEventId === eventId
              ? (participantOrder[eventId] || []).map((participant, index) => (
                  <div
                    key={participant.id}
                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 cursor-move hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    draggable
                    onDragStart={e => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', index);
                    }}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      e.preventDefault();
                      const from = Number(e.dataTransfer.getData('text/plain'));
                      const to = index;
                      if (from === to) return;
                      const updated = [...participantOrder[eventId]];
                      const [removed] = updated.splice(from, 1);
                      updated.splice(to, 0, removed);
                      setParticipantOrder({ ...participantOrder, [eventId]: updated });
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-primary dark:bg-primary-light text-white text-xs font-semibold">
                        #{participant.participantNumber}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 dark:text-slate-100 text-sm truncate">{participant.name}</div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <img
                            src={process.env.PUBLIC_URL + "/images/flags/" + (participant.nation?.code || 'DE') + ".png"}
                            alt={participant.nation?.name}
                            className="w-4 h-4 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                          />
                          <span className="truncate">{participant.nation?.name}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              : participants.map((participant, index) => (
                  <div key={participant.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold">
                        #{participant.participantNumber}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 dark:text-slate-100 text-sm truncate">{participant.name}</div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <img
                            src={process.env.PUBLIC_URL + "/images/flags/" + (participant.nation?.code || 'DE') + ".png"}
                            alt={participant.nation?.name}
                            className="w-4 h-4 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                          />
                          <span className="truncate">{participant.nation?.name}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      );
    }
  };

  // Handler für Zeitplan-Erstellung/Neu-Erstellung
  const handleGenerateSchedule = () => {
    setConfirmationType('generate');
    setShowConfirmation(true);
  };

  const handleUpdateTimes = () => {
    setConfirmationType('update-times');
    setShowConfirmation(true);
  };

  const handleConfirmGenerate = async () => {
    setIsGenerating(true);
    setShowConfirmation(false);
    
    try {
      let result;
      
      if (confirmationType === 'generate') {
        result = await scheduleService.generateSchedule();
      } else {
        result = await scheduleService.updateScheduleTimes();
      }
      
      if (result.success) {
        addToast('success', result.message || (confirmationType === 'generate' ? 'Zeitplan erfolgreich erstellt' : 'Zeiten erfolgreich aktualisiert'));
        await loadScheduleData();
        await checkExistingEvents();
      } else {
        addToast('error', result.error || (confirmationType === 'generate' ? 'Fehler beim Erstellen des Zeitplans' : 'Fehler beim Aktualisieren der Zeiten'));
      }
    } catch (error) {
      addToast('error', confirmationType === 'generate' ? 'Fehler beim Erstellen des Zeitplans' : 'Fehler beim Aktualisieren der Zeiten');
      console.error('Fehler:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  // Toast Handler
  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  // PDF Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const filters = {
        day: selectedDay,
        category: selectedCategory,
        status: selectedStatus,
        gender: selectedGender,
        searchTerm: searchTerm
      };

      const result = await pdfExportService.exportSchedulePDF(filters);
      
      if (result.success) {
        addToast('success', 'Zeitplan-PDF erfolgreich heruntergeladen');
      } else {
        addToast('error', result.error || 'Fehler beim PDF-Export');
      }
    } catch (error) {
      addToast('error', 'Fehler beim PDF-Export');
      console.error("Fehler beim PDF-Export:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Turnier starten
  const handleStartTournament = async () => {
    setIsStartingTournament(true);
    try {
      const result = await scheduleService.startTournament();
      
      if (result.success) {
        addToast('success', result.message);
        await loadScheduleData();
      } else {
        addToast('error', `Fehler beim Starten des Turniers: ${result.error}`);
      }
    } catch (error) {
      console.error('Fehler beim Starten des Turniers:', error);
      addToast('error', 'Fehler beim Starten des Turniers');
    } finally {
      setIsStartingTournament(false);
    }
  };

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64"></div>
                <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
              </div>
              <div className="text-right">
                <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-1"></div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-12"></div>
              </div>
            </div>
          </div>

          {/* Controls Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
              </div>
              {/* View Mode Toggle */}
              <div className="flex items-center gap-2">
                <div className="w-24 h-[38px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
                <div className="w-28 h-[38px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
              </div>
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <div className="w-40 h-[38px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
                <div className="w-32 h-[38px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
                <div className="w-36 h-[38px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
              </div>
            </div>
          </div>

          {/* Filters Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="space-y-6">
              {/* Day Filter */}
              <div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-3"></div>
                <div className="flex flex-wrap gap-1.5">
                  {[...Array(3)].map((_, j) => (
                    <div key={j} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-24"></div>
                  ))}
                </div>
              </div>
              {/* Category Filter */}
              <div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-3"></div>
                <div className="flex flex-wrap gap-1.5">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-24"></div>
                  ))}
                </div>
              </div>
              {/* Status Filter */}
              <div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-3"></div>
                <div className="flex flex-wrap gap-1.5">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-24"></div>
                  ))}
                </div>
              </div>
              {/* Gender Filter */}
              <div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-3"></div>
                <div className="flex flex-wrap gap-1.5">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-24"></div>
                  ))}
                </div>
              </div>
            </div>
            {/* Participant Controls */}
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-3"></div>
              <div className="flex flex-wrap gap-2">
                <div className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-32"></div>
                <div className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-32"></div>
              </div>
            </div>
          </div>

          {/* Venues Skeleton - Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
                {/* Venue Header */}
                <div className="bg-slate-50 dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                  <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-32 mb-3"></div>
                  <div className="flex items-center gap-4">
                    <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                    <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
                    <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                  </div>
                </div>
                {/* Events */}
                <div className="p-6 space-y-4">
                  {[...Array(3)].map((_, j) => (
                    <div key={j} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                      {/* Event Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-48"></div>
                        <div className="flex gap-2">
                          <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded-full w-20"></div>
                          <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded-full w-16"></div>
                        </div>
                      </div>
                      {/* Event Title */}
                      <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-3"></div>
                      {/* Badges */}
                      <div className="flex gap-2 mb-3">
                        <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded-full w-20"></div>
                        <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded-full w-20"></div>
                      </div>
                      {/* Teilnehmeranzahl */}
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">{error}</p>
                <button 
                  onClick={loadScheduleData} 
                  className="bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white px-6 py-3 rounded-md transition-colors duration-200 font-medium"
                >
                  {t("admin.retry", "Erneut versuchen")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.schedule.title", "Zeitplan")}
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mt-2">
                  {t("admin.schedule.subtitle", "Verwalte alle Events und deren Zeitplan.")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{filteredSchedule.length}</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Events</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Controls Section */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder={t("admin.schedule.search", "Event oder Ort suchen...")}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 h-[46px] border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'cards'
                      ? 'bg-primary dark:bg-primary-light text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  <i className="fas fa-th-large mr-1"></i>
                  Karten
                </button>
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'timeline'
                      ? 'bg-primary dark:bg-primary-light text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  <i className="fas fa-timeline mr-1"></i>
                  Timeline
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                {isEditMode ? (
                  <>
                    <button 
                      className="bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                      onClick={handleSaveChanges}
                      disabled={isSaving}
                    >
                      <i className={`fas ${isSaving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                      {isSaving 
                        ? "Speichere..." 
                        : hasUnsavedChanges 
                          ? "Änderungen speichern *" 
                          : "Änderungen speichern"
                      }
                    </button>
                    <button 
                      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 px-4 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                      onClick={handleToggleEditMode}
                      disabled={isSaving}
                    >
                      <i className="fas fa-times"></i>
                      Abbrechen
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 px-4 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
                      onClick={handleToggleEditMode}
                    >
                      <i className="fas fa-edit"></i>
                      Bearbeitungsmodus
                    </button>
                    {hasExistingEvents && (
                      <button 
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 px-4 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                        onClick={handleUpdateTimes}
                        disabled={isGenerating}
                        title="Nur Zeiten basierend auf aktuellen Settings aktualisieren"
                      >
                        <i className={`fas ${isGenerating ? 'fa-spinner fa-spin' : 'fa-clock'}`}></i>
                        {isGenerating ? "Aktualisiere..." : "Zeiten aktualisieren"}
                      </button>
                    )}
                    <button 
                      className="bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                      onClick={handleGenerateSchedule}
                      disabled={isGenerating}
                      title={hasExistingEvents ? "Zeitplan komplett neu erstellen (alle manuellen Änderungen gehen verloren)" : "Zeitplan erstellen"}
                    >
                      <i className={`fas ${isGenerating ? 'fa-spinner fa-spin' : hasExistingEvents ? 'fa-sync' : 'fa-calendar-plus'}`}></i>
                      {isGenerating 
                        ? "Erstelle..." 
                        : hasExistingEvents 
                          ? "Zeitplan neu erstellen" 
                          : "Zeitplan erstellen"
                      }
                    </button>
                    {hasExistingEvents && (
                      <button 
                        className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                        onClick={handleStartTournament}
                        disabled={isStartingTournament}
                        title="Erste Events jeder Fläche in 'Aktiv' Status versetzen"
                      >
                        <i className={`fas ${isStartingTournament ? 'fa-spinner fa-spin' : 'fa-play'}`}></i>
                        {isStartingTournament ? "Starte..." : "Turnier starten"}
                      </button>
                    )}
                    <button 
                      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 px-4 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                      onClick={handleExportPDF}
                      disabled={isExporting}
                      title={t("admin.schedule.exportPDF", "Zeitplan als PDF exportieren")}
                    >
                      <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                      {isExporting 
                        ? t("admin.schedule.exporting", "Exportiere...") 
                        : t("admin.schedule.exportPDF", "PDF Export")
                      }
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Filter Groups */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            <div className="space-y-6">
              {/* Day Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Tag</h3>
                <div className="flex flex-wrap gap-1.5">
                  {dayOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleDayChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedDay === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Kategorie</h3>
                <div className="flex flex-wrap gap-1.5">
                  {categoryOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleCategoryChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedCategory === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Status</h3>
                <div className="flex flex-wrap gap-1.5">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleStatusChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedStatus === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Gender Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Geschlecht</h3>
                <div className="flex flex-wrap gap-1.5">
                  {genderOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleGenderChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedGender === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Participant Controls */}
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">Teilnehmer</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  onClick={handleExpandAll}
                  title="Alle Gruppen ausklappen"
                >
                  <i className="fas fa-expand-alt mr-1"></i> Alle ausklappen
                </button>
                <button
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  onClick={handleCollapseAll}
                  title="Alle Gruppen einklappen"
                >
                  <i className="fas fa-compress-alt mr-1"></i> Alle einklappen
                </button>
              </div>
            </div>
          </div>

          {/* Schedule View */}
          {viewMode === 'cards' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Object.entries(displayVenuesData).map(([venue, events]) => (
                <div key={venue} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                  {/* Venue Header */}
                  <div className="bg-slate-50 dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                          {venue}
                        </h3>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                            <i className="fas fa-calendar-alt"></i>
                            {events.length} {t("admin.schedule.events", "Events")}
                          </span>
                          <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                            <i className="fas fa-users"></i>
                            {getTotalParticipants(events)} {t("admin.schedule.athletes", "Athleten")}
                          </span>
                          <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                            <i className="fas fa-clock"></i>
                            {calculateTimeEstimate(events)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Events Timeline */}
                  <div className={`p-6 ${isEditMode ? 'bg-amber-50/30 dark:bg-amber-900/20' : ''}`}>
                    {events.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                          <i className="fas fa-calendar-times text-slate-400 dark:text-slate-500 text-2xl"></i>
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Events</h3>
                        <p className="text-slate-500 dark:text-slate-400">{t("admin.schedule.noEvents", "Keine Events an diesem Tag")}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {events.map((event, index) => {
                          const eventParticipants = getParticipantsForEvent(event);
                          const duration = calculateGroupDurationMinutes(event.startTime, event.endTime);
                          const isLunchBreak = event.isLunchBreak === true;
                          
                          // Prüfe ob Tagwechsel
                          const prevEvent = index > 0 ? events[index - 1] : null;
                          const isNewDay = prevEvent && event.startTime && prevEvent.startTime && 
                            new Date(event.startTime).toDateString() !== new Date(prevEvent.startTime).toDateString();
                          
                          return (
                            <React.Fragment key={event.id}>
                              {/* Tagwechsel-Marker */}
                              {isNewDay && (
                                <div className="flex items-center gap-4 my-6">
                                  <div className="flex-1 border-t border-slate-300 dark:border-slate-600"></div>
                                  <div className="px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-full">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                      {formatDate(event.startTime)}
                                    </span>
                                  </div>
                                  <div className="flex-1 border-t border-slate-300 dark:border-slate-600"></div>
                                </div>
                              )}
                              
                              <div
                                className={`${
                                  isLunchBreak 
                                    ? 'bg-slate-100 dark:bg-slate-700/50 border-slate-300 dark:border-slate-600' 
                                    : event.status === 'Aktiv'
                                      ? 'bg-green-50/50 dark:bg-green-900/20 border-2 border-green-400 dark:border-green-500 shadow-md'
                                      : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
                                } rounded-lg p-4 transition-all ${
                                  isLunchBreak ? '' : (isEditMode ? 'cursor-move hover:bg-slate-50 dark:hover:bg-slate-700 hover:shadow-md' : 'hover:shadow-sm')
                                } ${
                                  event.status === 'Aktiv' && !isLunchBreak ? 'hover:shadow-lg' : ''
                                } ${
                                  draggedEvent?.id === event.id ? 'opacity-50 scale-95' : ''
                                } ${
                                  dragOverVenue === venue && dragOverIndex === index ? 'ring-2 ring-primary/20 dark:ring-primary-light/20' : ''
                                }`}
                              draggable={isEditMode && !isLunchBreak}
                              onDragStart={isLunchBreak ? undefined : (e) => handleScheduleEventDragStart(e, event, venue, index)}
                              onDragOver={isLunchBreak ? undefined : (e) => handleScheduleEventDragOver(e, venue, index)}
                              onDragLeave={isLunchBreak ? undefined : handleScheduleEventDragLeave}
                              onDrop={isLunchBreak ? undefined : (e) => handleScheduleEventDrop(e, venue, index)}
                              onDragEnd={isLunchBreak ? undefined : handleScheduleEventDragEnd}
                            >
                              {/* Event Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                  {formatDate(event.startTime)} • {formatTime(event.startTime)} - {formatTime(event.endTime)}
                                </div>
                                {!isLunchBreak && (
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                      event.status === 'Ausstehend' ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                                      event.status === 'Aktiv' ? 'bg-white dark:bg-slate-800 text-green-600 dark:text-green-400 border border-green-300 dark:border-green-600 shadow-sm' :
                                      event.status === 'Vorbereitung' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                                      event.status === 'Abgeschlossen' ? 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300' :
                                      'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                                    }`}>
                                      <i className={`fas ${
                                        event.status === 'Aktiv' ? 'fa-play' : 
                                        event.status === 'Ausstehend' ? 'fa-clock' : 
                                        event.status === 'Vorbereitung' ? 'fa-hourglass-half' :
                                        'fa-check'
                                      } mr-1.5`}></i>
                                      {getStatusLabel(event.status)}
                                    </span>
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                                      <i className="fas fa-clock mr-1.5"></i>
                                      {calculateGroupDuration(event.startTime, event.endTime)}
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Event Content */}
                              {isLunchBreak ? (
                                <div className="text-center py-2">
                                  <h4 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center justify-center gap-2">
                                    <i className="fas fa-utensils text-slate-500 dark:text-slate-400"></i>
                                    Mittagspause
                                  </h4>
                                  <p className="text-sm text-slate-600 dark:text-slate-400">
                                    {calculateGroupDuration(event.startTime, event.endTime)}
                                  </p>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {/* Titel - nur bis zum ersten "-" */}
                                  <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                    {(() => {
                                      const fullName = event.group?.name || 'Unbekanntes Event';
                                      const dashIndex = fullName.indexOf('-');
                                      return dashIndex > 0 ? fullName.substring(0, dashIndex).trim() : fullName;
                                    })()}
                                  </h4>
                                
                                {/* Badges für Altersgruppe und Geschlecht */}
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                    (() => {
                                      const ageGroupName = event.group?.ageGroup?.name;
                                      const colors = getAgeGroupColors(ageGroupName);
                                      const darkModeMap = {
                                        'Kinder': 'dark:bg-emerald-900/30 dark:text-emerald-300',
                                        'Jugend': 'dark:bg-blue-900/30 dark:text-blue-300',
                                        'Erwachsene': 'dark:bg-violet-900/30 dark:text-violet-300',
                                        'Senioren': 'dark:bg-amber-900/30 dark:text-amber-300',
                                        'Senioren Plus': 'dark:bg-red-900/30 dark:text-red-300'
                                      };
                                      const darkModeClasses = darkModeMap[ageGroupName] || 'dark:bg-slate-700 dark:text-slate-300';
                                      return `${colors.bgLight} ${colors.text} ${darkModeClasses}`;
                                    })()
                                  }`}>
                                    {event.group?.ageGroup?.name || 'Unbekannt'}
                                  </span>
                                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                    event.group?.gender === 'männlich' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                                  }`}>
                                    {event.group?.gender || 'Unbekannt'}
                                  </span>
                                </div>
                                
                                {/* Teilnehmeranzahl links, Ausklappen-Button rechts */}
                                <div className="flex items-center justify-between pt-1">
                                  <span className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                                    <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
                                    {(() => {
                                      const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');
                                      if (isTeamEvent && teamData && teamData.currentParticipants) {
                                        return `${teamData.currentParticipants} Teilnehmer`;
                                      }
                                      return `${eventParticipants.length} Teilnehmer`;
                                    })()}
                                  </span>
                                  <button 
                                    className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 flex items-center gap-1.5 transition-colors px-2 py-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
                                    onClick={() => handleToggleExpand(event.id)}
                                  >
                                    <i className={`fas ${expandedEvents.has(event.id) ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`}></i>
                                    {expandedEvents.has(event.id) ? "Ausblenden" : "Anzeigen"}
                                  </button>
                                </div>
                                
                                {/* Participants List mit Trennlinie und Sortier-Button */}
                                {expandedEvents.has(event.id) && (
                                  <div className="pt-3 mt-3 border-t border-slate-200 dark:border-slate-700">
                                    {/* Header mit Teilnehmerliste und Sortier-Button */}
                                    <div className="flex items-center justify-between mb-3">
                                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Teilnehmerliste</span>
                                      {event.status === 'Ausstehend' && (
                                        sortingEventId === event.id ? (
                                          <div className="flex items-center gap-2">
                                            <button 
                                              className="px-3 py-1.5 text-xs font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-md transition-colors flex items-center gap-1.5"
                                              title="Reihenfolge speichern" 
                                              onClick={() => handleSaveSorting(event.id)}
                                            >
                                              <i className="fas fa-save"></i>
                                              Speichern
                                            </button>
                                            <button 
                                              className="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors flex items-center gap-1.5"
                                              title="Abbrechen" 
                                              onClick={handleCancelSorting}
                                            >
                                              <i className="fas fa-times"></i>
                                              Abbrechen
                                            </button>
                                          </div>
                                        ) : (
                                          <button 
                                            className="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors flex items-center gap-1.5"
                                            title="Teilnehmer-Reihenfolge anpassen" 
                                            onClick={() => handleStartSorting(event.id, eventParticipants)}
                                          >
                                            <i className="fas fa-sort"></i>
                                            Reihenfolge bearbeiten
                                          </button>
                                        )
                                      )}
                                    </div>
                                    {renderParticipantsList(event.id, eventParticipants)}
                                  </div>
                                )}
                              </div>
                              )}
                            </div>
                            </React.Fragment>
                          );
                        })}
                        
                        {/* Drop Zone am Ende der Venue */}
                        {isEditMode && (
                          <div
                            className={`border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center transition-colors ${
                              dragOverVenue === venue && dragOverIndex === (isEditMode ? venuesData[venue].length : events.length) 
                                ? 'border-primary dark:border-primary-light bg-slate-50 dark:bg-slate-700' 
                                : 'hover:border-slate-400 dark:hover:border-slate-500'
                            }`}
                            onDragOver={(e) => handleScheduleEventDragOver(e, venue, isEditMode ? venuesData[venue].length : events.length)}
                            onDragLeave={handleScheduleEventDragLeave}
                            onDrop={(e) => handleScheduleEventDrop(e, venue, isEditMode ? venuesData[venue].length : events.length)}
                          >
                            <i className="fas fa-plus text-slate-400 dark:text-slate-500 text-2xl mb-2"></i>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Event hier ablegen</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Timeline View - Outlook Style */
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="bg-slate-50 dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Timeline-Ansicht</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Outlook-Kalender-ähnliche Darstellung mit beiden Flächen nebeneinander</p>
              </div>
              
              <div className="p-6">
                {(() => {
                  const timelineData = getTimelineData();
                  
                  if (timelineData.events.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                          <i className="fas fa-calendar-times text-slate-400 dark:text-slate-500 text-2xl"></i>
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Events</h3>
                        <p className="text-slate-500 dark:text-slate-400">Keine Events für die ausgewählten Filter</p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="space-y-4">
                      {/* Timeline Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                          {formatTimeForTimeline(timelineData.earliestTime)} - {formatTimeForTimeline(timelineData.latestTime)}
                        </div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                          {timelineData.events.length} Events
                        </div>
                      </div>
                      
                      {/* Outlook-Style Timeline Container */}
                      <div className="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                        {/* Venues - Side by Side */}
                        <div className="flex">
                          {/* Time Column */}
                          <div className="w-24 bg-slate-50/50 dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700">
                            <div className="flex flex-col" style={{ height: `${timelineData.timelineHeight}px` }}>
                              {(() => {
                                // Immer 5-Minuten-Intervalle für feine Zeitachse
                                const intervalMinutes = 5;
                                
                                const intervals = [];
                                const startTime = timelineData.earliestTime.getTime();
                                const endTime = timelineData.latestTime.getTime();
                                
                                for (let time = startTime; time <= endTime; time += intervalMinutes * 60 * 1000) {
                                  intervals.push(new Date(time));
                                }
                                
                                return intervals.map((time, i) => (
                                  <div key={i} className="flex-1 flex items-center justify-center border-b border-slate-100 dark:border-slate-700">
                                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                      {formatTimeForTimeline(time)}
                                    </span>
                                  </div>
                                ));
                              })()}
                            </div>
                          </div>
                          
                          {/* Venues Grid */}
                          <div className="flex-1 flex">
                            {Object.entries(displayVenuesData).map(([venue, events]) => (
                              <div key={venue} className="flex-1 border-r border-slate-200 dark:border-slate-700 last:border-r-0">
                                {/* Venue Header */}
                                <div className="h-10 bg-slate-50/50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-center">
                                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{venue}</span>
                                </div>
                                
                                {/* Events Container */}
                                <div 
                                  className="relative"
                                  style={{ height: `${timelineData.timelineHeight}px` }}
                                >
                                  {/* Tagwechsel-Marker */}
                                  {events.map((event, eventIndex) => {
                                    if (eventIndex === 0 || !event.startTime) return null;
                                    const prevEvent = events[eventIndex - 1];
                                    if (!prevEvent?.startTime) return null;
                                    
                                    const eventDate = new Date(event.startTime);
                                    const prevEventDate = new Date(prevEvent.startTime);
                                    
                                    // Prüfe ob Tagwechsel (nur wenn nicht Mittagspause)
                                    if (eventDate.toDateString() !== prevEventDate.toDateString() && !event.isLunchBreak) {
                                      const dayStart = new Date(eventDate);
                                      dayStart.setHours(0, 0, 0, 0);
                                      const dayStartPosition = ((dayStart.getTime() - timelineData.earliestTime.getTime()) / timelineData.totalDuration) * 100;
                                      
                                      return (
                                        <div
                                          key={`day-divider-${event.id}`}
                                          className="absolute left-0 right-0 z-10 pointer-events-none"
                                          style={{
                                            top: `${dayStartPosition}%`,
                                            height: '2px'
                                          }}
                                        >
                                          <div className="relative h-full">
                                            <div className="absolute left-0 right-0 h-full bg-slate-300 dark:bg-slate-600"></div>
                                            <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 top-1/2 px-3 py-1 bg-slate-100 dark:bg-slate-700 rounded-full border border-slate-300 dark:border-slate-600">
                                              <span className="text-xs font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                                                {formatDate(event.startTime)}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                  
                                  {events.map((event) => {
                                    const position = getEventPosition(event, timelineData);
                                    const eventParticipants = getParticipantsForEvent(event);
                                    const isLunchBreak = event.isLunchBreak === true;
                                    
                                    return (
                                      <div
                                        key={event.id}
                                        className={`absolute rounded-lg border transition-all ${
                                          isLunchBreak ? 'cursor-default' : (isEditMode ? 'cursor-move hover:shadow-lg' : 'hover:shadow-md')
                                        } ${
                                          event.status === 'Aktiv' && !isLunchBreak ? 'border-2 shadow-lg ring-2 ring-green-400/50 dark:ring-green-500/50' : ''
                                        } ${
                                          draggedEvent?.id === event.id ? 'opacity-50 scale-95' : ''
                                        }`}
                                        style={{
                                          top: `${position.left}%`,
                                          height: `${position.width}%`,
                                          minHeight: '100px', // 5 Minuten = 100px (20px pro Minute)
                                          width: 'calc(100% - 8px)',
                                          left: '4px',
                                          right: '4px'
                                        }}
                                        draggable={isEditMode && !isLunchBreak}
                                        onDragStart={isLunchBreak ? undefined : (e) => handleScheduleEventDragStart(e, event, venue, events.indexOf(event))}
                                        onDragOver={isLunchBreak ? undefined : (e) => handleScheduleEventDragOver(e, venue, events.indexOf(event))}
                                        onDragLeave={isLunchBreak ? undefined : handleScheduleEventDragLeave}
                                        onDrop={isLunchBreak ? undefined : (e) => handleScheduleEventDrop(e, venue, events.indexOf(event))}
                                        onDragEnd={isLunchBreak ? undefined : handleScheduleEventDragEnd}
                                      >
                                        {isLunchBreak ? (
                                          <div className="p-3 h-full rounded-lg shadow-sm bg-slate-100 dark:bg-slate-700/50 border-slate-300 dark:border-slate-600 flex items-center justify-center">
                                            <div className="text-center">
                                              <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1 flex items-center justify-center gap-2">
                                                <i className="fas fa-utensils text-slate-500 dark:text-slate-400"></i>
                                                Mittagspause
                                              </div>
                                              <div className="text-xs text-slate-600 dark:text-slate-400">
                                                {formatTimeForTimeline(new Date(event.startTime))} - {formatTimeForTimeline(new Date(event.endTime))}
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className={`p-3 h-full rounded-lg shadow-sm ${
                                            event.status === 'Aktiv'
                                              ? 'bg-green-100 dark:bg-green-900/40 border-green-300 dark:border-green-600 hover:bg-green-200 dark:hover:bg-green-900/50'
                                              : event.venue === 'Fläche A' 
                                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30' 
                                                : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30'
                                          }`}>
                                            <div className="space-y-2">
                                              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                                                {event.group?.name || 'Unbekannt'}
                                              </div>
                                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                                {formatTimeForTimeline(new Date(event.startTime))} - {formatTimeForTimeline(new Date(event.endTime))}
                                              </div>
                                              <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                                                  (() => {
                                                    const ageGroupName = event.group?.ageGroup?.name;
                                                    const colors = getAgeGroupColors(ageGroupName);
                                                    const darkModeMap = {
                                                      'Kinder': 'dark:bg-emerald-900/30 dark:text-emerald-300',
                                                      'Jugend': 'dark:bg-blue-900/30 dark:text-blue-300',
                                                      'Erwachsene': 'dark:bg-violet-900/30 dark:text-violet-300',
                                                      'Senioren': 'dark:bg-amber-900/30 dark:text-amber-300',
                                                      'Senioren Plus': 'dark:bg-red-900/30 dark:text-red-300'
                                                    };
                                                    const darkModeClasses = darkModeMap[ageGroupName] || 'dark:bg-slate-700 dark:text-slate-300';
                                                    return `${colors.bgLight} ${colors.text} ${darkModeClasses}`;
                                                  })()
                                                }`}>
                                                  {event.group?.ageGroup?.name || 'N/A'}
                                                </span>
                                                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                                                  event.group?.gender === 'männlich' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                                                }`}>
                                                  {event.group?.gender === 'männlich' ? 'Männlich' : 'Weiblich'}
                                                </span>
                                                <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                                  <i className="fas fa-users text-slate-400 dark:text-slate-500"></i>
                                                  {(() => {
                                                    const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');
                                                    if (isTeamEvent && teamData && teamData.currentParticipants) {
                                                      return teamData.currentParticipants;
                                                    }
                                                    return eventParticipants.length;
                                                  })()}
                                                </span>
                                              </div>
                                              <div className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                                                event.status === 'Ausstehend' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                                                event.status === 'Aktiv' ? 'bg-white dark:bg-slate-800 text-green-600 dark:text-green-400 border border-green-300 dark:border-green-600 shadow-sm' :
                                                event.status === 'Vorbereitung' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                                                event.status === 'Abgeschlossen' ? 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300' :
                                                'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                                              }`}>
                                                <i className={`fas ${
                                                  event.status === 'Aktiv' ? 'fa-play' : 
                                                  event.status === 'Ausstehend' ? 'fa-clock' : 
                                                  event.status === 'Vorbereitung' ? 'fa-hourglass-half' :
                                                  'fa-check'
                                                } mr-1.5`}></i>
                                                {getStatusLabel(event.status)}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmGenerate}
        title={confirmationType === 'generate' 
          ? (hasExistingEvents ? "Zeitplan neu erstellen" : "Zeitplan erstellen")
          : "Zeiten aktualisieren"}
        subtitle={confirmationType === 'generate' 
          ? "Diese Aktion kann nicht rückgängig gemacht werden." 
          : "Die Reihenfolge und Flächenzuordnung der Events bleibt erhalten."}
        message={confirmationType === 'generate'
          ? (hasExistingEvents 
              ? "Möchten Sie den Zeitplan wirklich neu erstellen? Alle bestehenden Events werden gelöscht und basierend auf den aktuellen Settings neu generiert."
              : "Möchten Sie einen neuen Zeitplan basierend auf den aktuellen Settings erstellen?")
          : "Möchten Sie die Zeiten im bestehenden Zeitplan basierend auf den aktuellen Settings aktualisieren?"}
        confirmText={confirmationType === 'generate' 
          ? (hasExistingEvents ? "Neu erstellen" : "Erstellen")
          : "Zeiten aktualisieren"}
        cancelText="Abbrechen"
        type={confirmationType === 'generate' ? "warning" : "info"}
        layout="inline"
        isLoading={isGenerating}
      />

      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-4 py-3 rounded-lg border shadow-lg bg-white dark:bg-slate-800 ${
            toast.type === 'success' ? 'border-green-200 dark:border-green-800' :
            toast.type === 'error' ? 'border-red-200 dark:border-red-800' :
            'border-blue-200 dark:border-blue-800'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                toast.type === 'success' ? 'bg-green-100 dark:bg-green-900/30' :
                toast.type === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
                'bg-blue-100 dark:bg-blue-900/30'
              }`}>
                <i className={`fas text-xs ${
                  toast.type === 'success' ? 'fa-check text-green-600 dark:text-green-400' :
                  toast.type === 'error' ? 'fa-times text-red-600 dark:text-red-400' :
                  'fa-info text-blue-600 dark:text-blue-400'
                }`}></i>
              </div>
              <span className={`text-sm font-medium ${
                toast.type === 'success' ? 'text-green-900 dark:text-green-300' :
                toast.type === 'error' ? 'text-red-900 dark:text-red-300' :
                'text-blue-900 dark:text-blue-300'
              }`}>{toast.message}</span>
              <button
                onClick={hideToast}
                className="ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduleTailwind;
